import * as React from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { ProtectedPageWrapper } from '../../../designSystem/layouts/pageWrappers/ProtectedPageWrapper';
import { SafetyInspection, LocationInspectionPointList, Activity, PublicAmorgUser, SafetyInspectionLocation } from '../../../amorg-common';
import { getActivity, updateActivity } from '../../../api/activityClientApi';
import { getLocations } from '../../../api/locationClientApi';
import { getSafetyInspection, getAvailableLocationInspectionPoints, createAllLocationInspections } from '../../../api/safetyInspectionClientApi';
import { getUsers } from '../../../api/userClientApi';
import { Button } from '../../../designSystem/button/Button';
import { Card } from '../../../designSystem/Card';
import { CollapsiblePanel } from '../../../designSystem/collapsible/CollapsiblePanel';
import { Flex } from '../../../designSystem/compositeComponents/FlexHelpers';
import { ListRow } from '../../../designSystem/compositeComponents/ListRow';
import { ReferenceFilesPanelCollapsible } from '../../../designSystem/compositeComponents/ReferenceFilesPanel';
import { TitleContainer } from '../../../designSystem/compositeComponents/TitleContainer';
import { DeviceView } from '../../../designSystem/DeviceView';
import { Divider } from '../../../designSystem/Divider';
import { Icon } from '../../../designSystem/icons/Icons';
import { SingleColumnLayout } from '../../../designSystem/layouts/SingleColumnLayout';
import { LoadingPageOverlay } from '../../../designSystem/LoadingPageOverlay';
import { P } from '../../../designSystem/typography/Body';
import { H3 } from '../../../designSystem/typography/Heading';
import { LocationPanel } from './_components/LocationPanel';
import { ActivityDetailsPanel } from '../../_components/activities/ActivityDetailsPanel';
import { ActivityHeader } from '../../_components/activities/ActivityHeader';

export default function SafetyInspectionPage(): JSX.Element {
    
    const params = useParams<{ activityId: string }>();
    return (
        <ProtectedPageWrapper requiredPermissions={['ViewActivity']} selectedMenuItemKey="safetyinspection">
            {params.activityId 
                ? <SafetyInspectionPageView activityId={params.activityId} />
                : <span>ugyldig url</span>
        }
        </ProtectedPageWrapper>
    );
}

type SafetyInspectionPageViewProps =
    {
        activityId: string
    };
function SafetyInspectionPageView (props: SafetyInspectionPageViewProps): JSX.Element 
{
    const [inspection, setInspection] = React.useState<SafetyInspection>(null);
    const [availableInspections, setAvailableInspections] = React.useState<LocationInspectionPointList[]>(null);
    const [expandedLocationIds, setExpandedLocationIds] = React.useState<string[]>([]);
    const [loading, setLoading] = React.useState(true);
    const [activity, setActivity] = React.useState<Activity>(null);
    const [users, setUsers] = React.useState<PublicAmorgUser[]>([]);
    const [inspectionPointRedirect, setInspectionPointRedirect] = React.useState(false);

    React.useEffect(() =>
    {
        const load = async () =>
        {
            setLoading(true);
            const act = await getActivity(props.activityId);
            if(!act)
            {
                setLoading(false);
                return;
            }
            setActivity(act);
            setUsers(await getUsers());
            setInspection(await getSafetyInspection(act.id));
            setAvailableInspections(await getAvailableLocationInspectionPoints());

            const locations = await getLocations();
            setExpandedLocationIds(locations.map(l => l.id));
            setInspection(await getSafetyInspection(props.activityId));
            setLoading(false);
        };
        load();
    }, [props.activityId]);


    async function handleClickCreateInspections (locationId: string)
    {
        setLoading(true);

        await createAllLocationInspections(activity.id, locationId);
        setInspection(await getSafetyInspection(activity.id));

        setLoading(false);
    }

    function handleToggleExpand (locationId: string, expanded: boolean)
    {
        setExpandedLocationIds(
            expanded
                ? [...expandedLocationIds, locationId]
                : expandedLocationIds.filter(id => id !== locationId)
        );
    }

    async function handleUpdate ()
    {
        setLoading(true);
        const act = await getActivity(props.activityId);
        if(!act)
        {
            setLoading(false);
            return;
        }   
        setActivity(act);
        setUsers(await getUsers());
        setInspection(await getSafetyInspection(activity.id));
        setAvailableInspections(await getAvailableLocationInspectionPoints());
        setLoading(false);
    }

    const locationsWithInspections = inspection?.locations.filter(l => l.facilities.some(f => f.inspectionPoints.length > 0)) || [];
    let availableLocations: SafetyInspectionLocation[] = [];
    if (availableInspections && inspection)
        availableLocations = inspection.locations.filter(l =>
            l.facilities.every(f => f.inspectionPoints.length === 0)
            && availableInspections.some(al => al.locationId === l.locationId && al.facilities.some(f => f.inspectionPoints.length > 0))
        ) || [];

    async function updateTitle (value: string)
    {
        await updateActivity(activity.id, value, activity.description, activity.start, activity.end, activity.location);
        handleUpdate();
    }

    const sharedComponent = 
    activity &&
    <CollapsiblePanel
        defaultOpen={true}
        openHeader={
            <TitleContainer title={activity.title} updateTitle={updateTitle} />
        }
        closedOrDefaultHeader={
            <TitleContainer title={activity.title} updateTitle={updateTitle} />
        }>
            <Card variant="outlined">
                <ActivityDetailsPanel
                    activity={activity}
                    descriptionText="Beskrivelse"
                    periodSelection='date-time'
                    onActivityUpdated={handleUpdate}
                    componentTitle='Møde info'
                    users={users}
                    infoComponents={<></>}
                    participantTexts={{ header: 'Ansvarlige' }} />

                    <ReferenceFilesPanelCollapsible objectId={activity.id} referenceType="SafetyInspection" />
            </Card>
            
    </CollapsiblePanel>

    return (
        <>
            <LoadingPageOverlay loading={loading} />

            {inspectionPointRedirect &&
                <Navigate to={`/inspectionpoints?safetyInspectionId=${activity.id}/`} />
            }

            {activity &&
                <SingleColumnLayout
                    noDivider
                    contentContainer={
                        <>
                            <ActivityHeader
                                activity={activity}
                                deleteConfirmationTextInModal="Tilhørende handlepunkter vil ligeledes blive slettet."
                                additionalToolBarButtons={[
                                    { variant: 'text', icon:'navigation_settings', tooltip: 'Indstilling af kontrolpunkter', 
                                        onClick: () => { setInspectionPointRedirect(true) } 
                                    },
                                ]} />

                            <DeviceView
                                tablet={sharedComponent}
                                desktop={sharedComponent} />

                            <Flex flexDirection='column' gap={4}>
                                <H3 color="gray">VALGTE LOKATIONER</H3>
                                <Divider />
                                {locationsWithInspections.length === 0 &&
                                    <P>Ingen lokationer tilføjet</P>
                                }
                                <div>
                                    {locationsWithInspections.map(l =>
                                    {
                                        return (
                                            <LocationPanel
                                                key={l.locationId}
                                                activityId={activity.id}
                                                location={l}
                                                expanded={expandedLocationIds.includes(l.locationId)}
                                                onToggleExpand={handleToggleExpand}
                                                onClickCreate={handleClickCreateInspections}
                                                onUpdate={handleUpdate} />
                                        );
                                    })}
                                </div>
                            </Flex>

                            <Flex flexDirection='column' gap={4}>
                                <H3 color="gray">ØVRIGE LOKATIONER</H3>
                                <Divider />
                                {availableLocations.length === 0 && locationsWithInspections.length === 0 &&
                                    <P>Der skal oprettes lokationer for at kunne afvikle sikkerhedsrunderinger</P>
                                }
                                {availableLocations.length === 0 && locationsWithInspections.length > 0 &&
                                    <P>Alle lokationer er tilføjet</P>
                                }
                                {availableLocations.map(l =>
                                    <ListRow key={l.locationId} styleVariant='normal'>
                                        <Flex justifyContent='stretch'>
                                            <Flex gap={16} alignItems='center'>
                                                <Icon name='facility_domain' size='small' />
                                                <H3>{l.name}</H3>
                                            </Flex>
                                            <Flex justifyContent='flex-end' style={{ flexGrow: 1 }}>
                                                <Button variant='filled' text='Tilføj' onClick={() => handleClickCreateInspections(l.locationId)} />
                                            </Flex>
                                        </Flex>
                                    </ListRow>
                                )}
                            </Flex>
                        </>
                    } />
            }
        </>
    );
}